import { useState, useEffect, useRef, useCallback } from 'react';
import { ChevronsUpDown, Plus } from 'lucide-react';
import { cn } from '../../lib/utils';

export const SearchableSelect = ({
  options = [],
  value,
  onChange,
  onCreateNew,
  placeholder = "Select...",
  searchPlaceholder = "Type to search...",
  createNewLabel = "Create",
  disabled = false,
  className,
  'data-testid': dataTestId
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  const selectedOption = options.find(opt => opt.value === value);

  const filteredOptions = options.filter(opt =>
    opt.label.toLowerCase().includes(search.toLowerCase())
  ).sort((a, b) => a.label.localeCompare(b.label));

  const showCreateNew = search.trim() && !filteredOptions.some(
    opt => opt.label.toLowerCase() === search.toLowerCase()
  );

  const totalItems = filteredOptions.length + (showCreateNew && onCreateNew ? 1 : 0);

  const justSelectedRef = useRef(false);

  const closeAndReset = useCallback(() => {
    setOpen(false);
    setSearch('');
    setHighlightedIndex(-1);
  }, []);

  const handleSelect = useCallback((optionValue) => {
    onChange(optionValue);
    justSelectedRef.current = true;
    closeAndReset();
    // Move focus to next field in the form
    const form = inputRef.current?.closest('form');
    if (form) {
      const focusable = Array.from(
        form.querySelectorAll('input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled])')
      ).filter(el => el.offsetParent !== null);
      const idx = focusable.indexOf(inputRef.current);
      if (idx >= 0 && idx < focusable.length - 1) {
        focusable[idx + 1].focus();
        return;
      }
    }
    inputRef.current?.blur();
  }, [onChange, closeAndReset]);

  const handleCreateNew = useCallback(() => {
    if (onCreateNew && search.trim()) {
      onCreateNew(search.trim());
      justSelectedRef.current = true;
      closeAndReset();
      // Move focus to next field in the form
      const form = inputRef.current?.closest('form');
      if (form) {
        const focusable = Array.from(
          form.querySelectorAll('input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled])')
        ).filter(el => el.offsetParent !== null);
        const idx = focusable.indexOf(inputRef.current);
        if (idx >= 0 && idx < focusable.length - 1) {
          focusable[idx + 1].focus();
          return;
        }
      }
      inputRef.current?.blur();
    }
  }, [onCreateNew, search, closeAndReset]);

  const handleFocus = () => {
    // Don't re-open if we just selected a value
    if (justSelectedRef.current) {
      justSelectedRef.current = false;
      return;
    }
    setOpen(true);
    setSearch('');
  };

  const handleClick = () => {
    // Always open on explicit click, even if already focused
    if (!open) {
      setOpen(true);
      setSearch('');
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Tab') {
      closeAndReset();
      return; // Let Tab propagate naturally
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!open) { setOpen(true); return; }
      setHighlightedIndex(prev => (prev + 1) % totalItems);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex(prev => (prev - 1 + totalItems) % totalItems);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (!open) { setOpen(true); return; }
      if (highlightedIndex >= 0 && highlightedIndex < filteredOptions.length) {
        // Select highlighted option
        handleSelect(filteredOptions[highlightedIndex].value);
      } else if (showCreateNew && onCreateNew && highlightedIndex === filteredOptions.length) {
        // Highlighted on "Create new" option
        handleCreateNew();
      } else if (showCreateNew && onCreateNew && filteredOptions.length === 0) {
        // Only "Create new" visible, no highlight needed
        handleCreateNew();
      } else if (filteredOptions.length > 0) {
        // No highlight — select first match
        handleSelect(filteredOptions[0].value);
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      closeAndReset();
      inputRef.current?.blur();
    }
  };

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        closeAndReset();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open, closeAndReset]);

  // Reset highlight when search changes
  useEffect(() => { setHighlightedIndex(-1); }, [search]);

  // Scroll highlighted item into view
  useEffect(() => {
    if (highlightedIndex >= 0 && listRef.current) {
      const items = listRef.current.querySelectorAll('[data-option-item]');
      items[highlightedIndex]?.scrollIntoView({ block: 'nearest' });
    }
  }, [highlightedIndex]);

  // Display text: when focused/open show search text, otherwise show selected label or raw value
  const displayValue = open ? search : (selectedOption ? selectedOption.label : (value || ''));

  return (
    <div ref={containerRef} className="relative w-full">
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-haspopup="listbox"
          disabled={disabled}
          placeholder={open ? searchPlaceholder : placeholder}
          value={displayValue}
          onFocus={handleFocus}
          onClick={handleClick}
          onChange={(e) => {
            setSearch(e.target.value);
            if (!open) setOpen(true);
          }}
          onKeyDown={handleKeyDown}
          autoComplete="off"
          className={cn(
            "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 pr-8 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
            className
          )}
          data-testid={dataTestId}
        />
        <ChevronsUpDown
          className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 opacity-50 pointer-events-none"
        />
      </div>

      {open && (
        <div className="absolute z-50 mt-1 w-full rounded-md border bg-popover text-popover-foreground shadow-md animate-in fade-in-0 zoom-in-95">
          <div ref={listRef} role="listbox" className="max-h-60 overflow-auto p-1">
            {filteredOptions.length === 0 && !showCreateNew && (
              <div className="py-6 text-center text-sm text-muted-foreground">
                No options found
              </div>
            )}
            {filteredOptions.map((option, idx) => (
              <div
                key={option.value}
                data-option-item
                role="option"
                aria-selected={value === option.value}
                className={cn(
                  "flex items-center gap-2 px-2 py-1.5 text-sm rounded-sm cursor-pointer",
                  value === option.value && "bg-primary/10 text-primary font-medium",
                  highlightedIndex === idx && value !== option.value && "bg-muted",
                  highlightedIndex !== idx && value !== option.value && "hover:bg-muted"
                )}
                onMouseDown={(e) => { e.preventDefault(); handleSelect(option.value); }}
                onMouseEnter={() => setHighlightedIndex(idx)}
                data-testid={`${dataTestId}-option-${option.value}`}
              >
                {option.label}
              </div>
            ))}
            {showCreateNew && onCreateNew && (
              <div
                data-option-item
                role="option"
                className={cn(
                  "flex items-center gap-2 px-2 py-1.5 text-sm rounded-sm cursor-pointer text-primary",
                  highlightedIndex === filteredOptions.length && "bg-muted",
                  highlightedIndex !== filteredOptions.length && "hover:bg-muted"
                )}
                onMouseDown={(e) => { e.preventDefault(); handleCreateNew(); }}
                onMouseEnter={() => setHighlightedIndex(filteredOptions.length)}
                data-testid={`${dataTestId}-create-new`}
              >
                <Plus className="h-4 w-4" />
                {createNewLabel} "{search}"
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
